import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthUser } from '@/types';

/**
 * Returns true iff the JWT's `exp` claim is in the future. Decodes the payload
 * (base64url, RFC 7515 §2) without verifying the signature — that's the server's
 * job. Used on hydrate so a STALE persisted token can't keep the portal
 * "logged in" with a dead token (every API call then 401s and the user is
 * stuck on a blank dashboard with no redirect to /login). OP-152.
 */
export function isTokenStillValid(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;

  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  login: (accessToken: string, refreshToken: string, user: AuthUser) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setUser: (user) =>
        set({ user }),

      login: (accessToken, refreshToken, user) =>
        set({
          accessToken,
          refreshToken,
          user,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: 'css-admin-auth',
      storage: createJSONStorage(() => localStorage),
      // Do NOT persist `isAuthenticated` — it used to be stored verbatim, so a
      // token expired days ago still skipped the /login redirect and the
      // dashboard loaded with every call 401-ing. Persist only credentials and
      // derive `isAuthenticated` from the token's `exp` on hydrate. OP-152
      // (mirrors the agent-console authStore fix).
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const valid = isTokenStillValid(state.accessToken);
        state.isAuthenticated = valid;
        if (!valid) {
          // Token already expired — scrub the useless credentials so the axios
          // interceptor doesn't send them.
          state.accessToken = null;
          state.refreshToken = null;
          state.user = null;
        }
      },
    }
  )
);
